import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import ChevronDown from 'wix-ui-icons-common/ChevronDown';

import { KEYBOARD_EVENT_KEYS } from '../../constants/keyboard-event-keys';
import { MaybeUndef } from '../../types/system';
import { EmptyButton } from '../common';
import DropdownLayout from '../common/dropdown-layout/dropdown-layout';

import styles from './labeled-dropdown.scss';
import { DropdownLayoutOption, Text } from '@wix/design-system';

const WIDTH_DEFAULT = 100;

interface Props {
  selectedId?: string | number;
  initialSelectedId?: string | number;
  options: DropdownLayoutOption[];
  buttonDataHook?: string;
  dropdownDataHook?: string;
  className?: string;
  labelClassName?: string;
  dropDirectionUp?: boolean;
  width?: number;

  onSelect(option: DropdownLayoutOption): void;
}

interface State {
  isDropdownVisible: boolean;
  selectedId: MaybeUndef<string | number>;
}

const NOT_HOVERED_INDEX = -1;

export class LabeledDropdown extends React.Component<Props, State> {
  public state: State;

  constructor(props: Props) {
    super(props);

    this.state = {
      isDropdownVisible: false,
      selectedId: undefined,
    };
  }

  public componentDidMount() {
    // TODO: handle this on wix-style-react side
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  public componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
  }

  public render() {
    const {
      buttonDataHook,
      className,
      options,
      dropDirectionUp,
      labelClassName,
      dropdownDataHook,
    } = this.props;
    const { isDropdownVisible } = this.state;

    const selectedId = this.getSelectedId();

    return (
      <div
        className={classNames(styles.dropdownWrapper, className)}
        data-hook={buttonDataHook}
      >
        <EmptyButton onClick={this.toggleVisibility.bind(this)}>
          <div className={classNames(styles.label, labelClassName)}>
            <Text skin="primary" size="small" className={styles.selectedValue}>
              {this.getSelectedValue(selectedId)}
            </Text>

            <ChevronDown />
          </div>
        </EmptyButton>

        <div
          className={classNames(styles.dropdown)}
          style={this.getDropdownWrapperStyle()}
        >
          <DropdownLayout
            dataHook={dropdownDataHook}
            withArrow
            dropDirectionUp={dropDirectionUp}
            selectedId={selectedId}
            visible={isDropdownVisible}
            options={options}
            onClickOutside={this.close.bind(this)}
            onSelect={this.fireSelect.bind(this)}
            maxHeightPixels={420}
          />
        </div>
      </div>
    );
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === KEYBOARD_EVENT_KEYS.ESCAPE) {
      this.close();
    }
  }

  private toggleVisibility(): void {
    this.setState({ isDropdownVisible: !this.state.isDropdownVisible });
  }

  private close(): void {
    this.setState({ isDropdownVisible: false });
  }

  private fireSelect(option: DropdownLayoutOption) {
    this.setState({
      selectedId: option.id,
    });
    this.props.onSelect(option);
    this.close();
  }

  private getSelectedId(): number | string {
    const { selectedId, initialSelectedId } = this.props;

    if (selectedId !== undefined) {
      return selectedId;
    }

    if (this.state.selectedId !== undefined) {
      return this.state.selectedId;
    }

    if (initialSelectedId !== undefined) {
      return initialSelectedId;
    }

    return NOT_HOVERED_INDEX;
  }

  private getSelectedValue(selectedId: string | number) {
    const { options } = this.props;
    return _.get(_.find(options, { id: selectedId }), 'value');
  }

  private getDropdownWrapperStyle() {
    const width =
      this.props.width === undefined ? WIDTH_DEFAULT : this.props.width;

    return {
      width: `${width}px`,
      left: `calc(-${width / 2}px + 50%)`,
    };
  }
}
